import { useT } from "../../shared/i18n/i18n";
import { ClipboardEvent, FormEvent, KeyboardEvent, MouseEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check, Pencil, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type EditableTextSubmitMode = "blur" | "both" | "enter" | "none";

type EditableTextProps = {
  ariaLabel: string;
  className?: string;
  editTrigger?: "click" | "doubleClick";
  inputClassName?: string;
  multiline?: boolean;
  onChange: (value: string) => void;
  onInputChange?: (value: string) => void;
  placeholder: string;
  previewClassName?: string;
  selectOnFocus?: boolean;
  showTriggers?: boolean;
  submitMode?: EditableTextSubmitMode;
  value: string;
};

export function EditableText({
  ariaLabel,
  className,
  editTrigger = "click",
  inputClassName,
  multiline = false,
  onChange,
  onInputChange,
  placeholder,
  previewClassName,
  selectOnFocus = false,
  showTriggers = false,
  submitMode = "both",
  value,
}: EditableTextProps) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const draftValueRef = useRef(value);
  const isEditingRef = useRef(false);
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value);
      draftValueRef.current = value;
    }
  }, [isEditing, value]);

  useLayoutEffect(() => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }

    if (!isEditing) {
      syncEditorText(editor, localValue);
      return;
    }

    syncEditorText(editor, draftValueRef.current);
    editor.focus({ preventScroll: true });
    if (selectOnFocus) {
      placeCaret(editor, "all");
    }
  }, [isEditing, localValue, selectOnFocus]);

  function startEdit() {
    if (isEditing) {
      return;
    }

    draftValueRef.current = localValue;
    setIsEditing(true);
  }

  function commitDraft() {
    if (!isEditingRef.current) {
      return;
    }

    const nextValue = sanitizeText(editorRef.current?.innerText ?? draftValueRef.current, multiline);
    draftValueRef.current = nextValue;
    setLocalValue(nextValue);
    onChange(nextValue);
    setIsEditing(false);
  }

  function cancelEdit() {
    draftValueRef.current = localValue;
    onInputChange?.(localValue);
    setIsEditing(false);
  }

  function handleBeforeInput(event: FormEvent<HTMLDivElement>) {
    const nativeEvent = event.nativeEvent as InputEvent;
    if (nativeEvent.inputType.startsWith("format")) {
      event.preventDefault();
      return;
    }

    if (!multiline && (nativeEvent.inputType === "insertParagraph" || nativeEvent.inputType === "insertLineBreak")) {
      event.preventDefault();
    }
  }

  function handleInput() {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }

    const nextValue = sanitizeText(editor.innerText, multiline);
    draftValueRef.current = nextValue;
    onInputChange?.(nextValue);

    if (editor.innerText !== nextValue) {
      editor.innerText = nextValue;
      placeCaret(editor, "end");
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    // While editing, keep keystrokes local — otherwise arrow keys bubble to
    // parent composites (e.g. Tabs) and move focus instead of the caret.
    if (isEditing) {
      event.stopPropagation();
    }

    if ((event.metaKey || event.ctrlKey) && ["b", "i", "u"].includes(event.key.toLowerCase())) {
      event.preventDefault();
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      cancelEdit();
      return;
    }

    if (event.key !== "Enter" || event.nativeEvent.isComposing) {
      return;
    }

    const shouldCommit = multiline ? !event.shiftKey || event.metaKey || event.ctrlKey : !event.shiftKey;
    if (!shouldCommit || (submitMode !== "both" && submitMode !== "enter")) {
      return;
    }

    event.preventDefault();
    commitDraft();
  }

  function handlePaste(event: ClipboardEvent<HTMLDivElement>) {
    event.preventDefault();
    const text = sanitizeText(event.clipboardData.getData("text/plain"), multiline);
    insertText(text);
    handleInput();
  }

  function handleBlur() {
    if (submitMode === "both" || submitMode === "blur") {
      commitDraft();
      return;
    }

    cancelEdit();
  }

  const isContentEditable = editTrigger === "click" || isEditing;

  return (
    <div className={cn("group/editable relative block min-w-0", className)}>
      <div className={cn("relative flex min-w-0 items-start gap-1", multiline && "flex-col")}>
        <div
          aria-label={ariaLabel}
          aria-multiline={multiline}
          aria-readonly={editTrigger === "doubleClick" && !isEditing ? true : undefined}
          className={cn(
            "min-w-0 flex-1 outline-none caret-current",
            isContentEditable ? "cursor-text" : "cursor-default",
            isEditing ? inputClassName : previewClassName,
            multiline ? "whitespace-pre-wrap break-words" : isEditing ? "overflow-x-auto whitespace-nowrap" : "truncate",
            !localValue.trim() && "empty:before:pointer-events-none empty:before:text-muted-foreground/55 empty:before:content-[attr(data-placeholder)]",
          )}
          contentEditable={isContentEditable ? "plaintext-only" : false}
          data-placeholder={placeholder}
          onClick={editTrigger === "click" ? startEdit : undefined}
          onDoubleClick={editTrigger === "doubleClick" ? startEdit : undefined}
          onFocus={editTrigger === "click" ? startEdit : undefined}
          onBeforeInput={handleBeforeInput}
          onBlur={handleBlur}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          ref={editorRef}
          role="textbox"
          spellCheck
          suppressContentEditableWarning
          tabIndex={isContentEditable || !isEditing ? 0 : undefined}
        />

        {showTriggers ? (
          multiline ? (
            <EditableTextFooterActions isEditing={isEditing} onCancel={cancelEdit} onSubmit={commitDraft} />
          ) : (
            <EditableTextInlineActions isEditing={isEditing} onCancel={cancelEdit} onEdit={startEdit} onSubmit={commitDraft} />
          )
        ) : null}
      </div>
    </div>
  );
}

function EditableTextInlineActions({
  isEditing,
  onCancel,
  onEdit,
  onSubmit,
}: {
  isEditing: boolean;
  onCancel: () => void;
  onEdit: () => void;
  onSubmit: () => void;
}) {
  const t = useT();
  return (
    <div className={cn("grid shrink-0 transition-opacity duration-150", isEditing ? "opacity-100" : "opacity-0 group-hover/editable:opacity-100")}>
      <span className={cn("col-start-1 row-start-1 transition-all duration-150", isEditing && "pointer-events-none scale-75 opacity-0")}>
        <Button aria-label={t("common.edit")} onClick={onEdit} onMouseDown={keepEditorFocus} size="icon-xs" type="button" variant="ghost">
          <Pencil />
        </Button>
      </span>
      <span className={cn("col-start-1 row-start-1 transition-all duration-150", !isEditing && "pointer-events-none scale-75 opacity-0")}>
        <Button aria-label={t("common.save")} onClick={onSubmit} onMouseDown={keepEditorFocus} size="icon-xs" type="button" variant="ghost">
          <Check />
        </Button>
      </span>
      <span
        className={cn(
          "col-start-2 row-start-1 overflow-hidden transition-all duration-200",
          isEditing ? "ml-1 max-w-5 translate-x-0 opacity-100" : "max-w-0 -translate-x-2 opacity-0",
        )}
      >
        <Button aria-label={t("common.cancel")} onClick={onCancel} onMouseDown={keepEditorFocus} size="icon-xs" type="button" variant="ghost">
          <X />
        </Button>
      </span>
    </div>
  );
}

function EditableTextFooterActions({
  isEditing,
  onCancel,
  onSubmit,
}: {
  isEditing: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const t = useT();
  if (!isEditing) {
    return null;
  }

  return (
    <div className="mt-2 flex w-full items-center justify-between gap-3 text-xs text-muted-foreground">
      <span className="min-w-0 flex-1">{t("common.shiftEnter")}</span>
      <div className="flex items-center gap-1">
        <Button aria-label={t("common.save")} onClick={onSubmit} onMouseDown={keepEditorFocus} size="icon-xs" type="button" variant="ghost">
          <Check />
        </Button>
        <Button aria-label={t("common.cancel")} onClick={onCancel} onMouseDown={keepEditorFocus} size="icon-xs" type="button" variant="ghost">
          <X />
        </Button>
      </div>
    </div>
  );
}

function keepEditorFocus(event: MouseEvent<HTMLButtonElement>) {
  event.preventDefault();
}

function sanitizeText(value: string, multiline: boolean) {
  const normalized = value.replace(/\r\n?/g, "\n");
  return multiline ? normalized : normalized.replace(/\n+/g, " ");
}

function insertText(text: string) {
  const selection = window.getSelection();
  if (!selection?.rangeCount) {
    return;
  }

  selection.deleteFromDocument();
  selection.getRangeAt(0).insertNode(document.createTextNode(text));
  selection.collapseToEnd();
}

function placeCaret(element: HTMLElement, mode: "all" | "end") {
  const selection = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(element);
  if (mode === "end") {
    range.collapse(false);
  }
  selection?.removeAllRanges();
  selection?.addRange(range);
}

function syncEditorText(editor: HTMLDivElement | null, nextValue: string) {
  if (editor && editor.textContent !== nextValue) {
    editor.textContent = nextValue;
  }
}
